import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { NoToneMapping } from "three/src/constants.js";

const eventOptions = [
  "Birthday",
  "Wedding",
  "Baby Shoot",
  "Outdoor Shoot",
  "Pre Wedding",
  "Post Wedding",
  "Meetings",
  "Others"
];

const clientMongoId = localStorage.getItem("clientMongoId"); // or however you get the client ID
console.log("Client ID:",clientMongoId);
function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    username: "",
    password: "",
    mobile: "",
    place: "",
    event: ""
  });
  const [userListOpen, setUserListOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterEvent, setFilterEvent] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const navigate = useNavigate();

  // Fetch users for this client
  useEffect(() => {
  if (!clientMongoId) return; // Don't fetch if clientMongoId is missing
  async function fetchUsers() {
    try {
      console.log("Fetching users for clientMongoId:", clientMongoId);
      const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/users`);
      setUsers(res.data);
    } catch (err) {
      // handle error
    }
  }
  fetchUsers();
}, [clientMongoId]);

  const handleAddUser = () => {
    setForm({
      name: "",
      username: "",
      password: "",
      mobile: "",
      place: "",
      event: "",
      date: "",
    });
    setOpen(true);
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const allFilled = Object.values(form).every((v) => v);

  // Add user to this client
  const handleCreate = async () => {
  if (!clientMongoId) {
    alert("Client ID not found. Please log in as a client.");
    return;
  }
  try {
    const res = await axios.post(
      `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users`,
      form
    );
    setUsers(prev => [...prev, res.data]);
    setOpen(false);
  } catch (err) {
    // handle error
  }
};
  const handleClose = () => setOpen(false);
  const handleOpenUserList = () => setUserListOpen(true);
  const handleCloseUserList = () => {
    setUserListOpen(false);
    setSearch("");
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredUsersAdvanced = users.filter(u => {
    // Search by name, event, or mobile
    const matchesSearch =
      u.name.toLowerCase().includes(filterSearch.toLowerCase()) ||
      u.event.toLowerCase().includes(filterSearch.toLowerCase()) ||
      u.mobile.includes(filterSearch);

    // Filter by event
    const matchesEvent = filterEvent ? u.event === filterEvent : true;

    // Filter by date (assume u.date is ISO string)
    const matchesDate = filterDate ? u.date && u.date.slice(0, 10) === filterDate : true;

    return matchesSearch && matchesEvent && matchesDate;
  });

  const cards = [
    {
      label: "Add User",
      icon: (
        <IconButton color="primary" sx={{ bgcolor: "#fff", boxShadow: 2 }}>
          <AddIcon fontSize="large" />
        </IconButton>
      ),
      count: null,
      color: "#1976d2",
      gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      onClick: handleAddUser
    },
    {
      label: "Number of Users",
      icon: <GroupsIcon fontSize="large" sx={{ color: "#1976d2", mb: 1 }} />,
      count: users.length,
      color: "#1976d2",
      gradient: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      onClick: handleOpenUserList
    },
   
  ];

  return (
    <Box sx={{ p: isMobile ? 1 : 4, backgroundImage: `url('https://wallpaperaccess.com/full/1868308.jpg')`, minHeight: "100vh", width: "95vw", position: "relative", marginTop: isMobile ? 0 : "0px" }}>
      {/* Header Section */}
      <Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 4,
    px: 2,
    py: 2,
     background: "linear-gradient(90deg, #1976d2 60%, #64b5f6 100%)",
    borderRadius: 4,
    boxShadow: 3,
  }}
>
  <GroupsIcon sx={{ color: "#ffffffff", fontSize: 40 }} />
  <Typography variant="h4" sx={{ fontWeight: 800, color: "#ffffffff", letterSpacing: 2,fontFamily:"Josefin Sans" }}>
    User Management
  </Typography>
</Box>

      {/* Top Dashboard Cards */}
      <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    gap: 4,
    mb: 4,
    flexWrap: "wrap"
  }}
>
  {cards.map((card, idx) => (
    <Card
      key={card.label}
      sx={{
        width: 280,
        height: 180,
        borderRadius: 4,
        boxShadow: 4,
        textAlign: "center",
        background: card.gradient,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-6px) scale(1.03)",
          boxShadow: 8,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: card.onClick ? "pointer" : "default",
      }}
      onClick={card.onClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
          {card.icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: card.color,fontFamily:"Josefin Sans",fontSize:"25px" }}>
          {card.label}
        </Typography>
        {card.count !== null && (
          <Typography variant="h4" sx={{ color: card.color, fontWeight: 800,fontFamily:"Josefin Sans" }}>
            {card.count}
          </Typography>
        )}
      </CardContent>
    </Card>
  ))}

  {/* Decorative/Info Card to fill space */}
  <Card
    sx={{
      width: 280,
      height: 180,
      borderRadius: 4,
      boxShadow: 4,
      textAlign: "center",
      background: "linear-gradient(135deg, #fffde7 0%, #ffe082 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CardContent>
      <Box sx={{ mb: 1 }}>
        <img
          src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
          alt="Welcome"
          style={{ width: 48, height: 48, opacity: 0.7 }}
        />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#d32f2f",fontFamily:"Josefin Sans" }}>
        Welcome!
      </Typography>
      <Typography variant="body2" sx={{ color: "#888",fontFamily:"Jost" }}>
        Manage your users easily.<br />
        Use the cards to add or view users.
      </Typography>
    </CardContent>
  </Card>
</Box>

      {/* Add User Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            textAlign: "center",
            fontWeight: 700,
            letterSpacing: 2,
            fontFamily:"Josefin Sans"
          }}
        >
          Add User
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleFormChange("name")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2,fontFamily:"Josefin Sans" }}
            />
            <TextField
              label="Username"
              value={form.username}
              onChange={handleFormChange("username")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2,fontFamily:"Josefin Sans" }}
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={handleFormChange("password")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}
            />
            <TextField
              label="Mobile Number"
              value={form.mobile}
              onChange={handleFormChange("mobile")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}
            />
            <TextField
              label="Place"
              value={form.place}
              onChange={handleFormChange("place")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}
            />
            <TextField
              select
              label="Event"
              value={form.event}
              onChange={handleFormChange("event")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}
            >
              {eventOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              type="date"
              value={form.date}
              onChange={handleFormChange("date")}
              fullWidth
              variant="outlined"
              sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={handleClose} sx={{ px: 4, fontWeight: 700,fontFamily:"Josefin Sans" }}>Close</Button>
          <Button
            variant="contained"
            sx={{ px: 4, fontWeight: 700, bgcolor: "#1976d2",fontFamily:"Josefin Sans" }}
            onClick={handleCreate}
            disabled={!allFilled}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* User List Dialog */}
      <Dialog open={userListOpen} onClose={handleCloseUserList} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            textAlign: "center",
            fontWeight: 700,
           fontFamily:"Josefin Sans",
           fontSize:"25px",
            letterSpacing: 2
          }}
        >
          User List
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, bgcolor: "#f5f5f5", borderRadius: 2, px: 2,mt:3 }}>
            <SearchIcon sx={{ color: "#1976d2", mr: 1,height:"50px" }} />
            <TextField
              placeholder="Search by name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              variant="standard"
              fullWidth
              InputProps={{ disableUnderline: true }}
              sx={{ bgcolor: "transparent" }}
            />
          </Box>
          <Stack spacing={2}>
            {filteredUsers.length === 0 ? (
              <Typography sx={{ textAlign: "center", color: "#888" }}>No users found.</Typography>
            ) : (
              filteredUsers.map(user => (
                <Box
                  key={user._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    bgcolor: "#e3f2fd",
                    borderRadius: 2,
                    boxShadow: 1,
                    cursor: "pointer",
                    fontFamily:"Josefin Sans"
                  }}
                  onClick={() => navigate(`/admin-home/user/${user._id}`)}
                >
                  <Typography sx={{ fontWeight: 600,fontFamily:"Josefin Sans" }}>{user.name}</Typography>
                  <Typography sx={{ color: "#4e4e4eff", fontWeight: 500,fontFamily:"Josefin Sans" }}>
                    {user.date ? user.date.slice(0, 10) : ""}
                  </Typography>
                  <Typography sx={{ color: "#1976d2", fontWeight: 500,fontFamily:"Josefin Sans" }}>{user.event}</Typography>
                </Box>
              ))
            )
            }
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={handleCloseUserList} sx={{ px: 4, fontWeight: 700,fontFamily:"Josefin Sans" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Search + Table in One Card */}
      <Card
        sx={{
          mt: 4,
          mb: 4,
          p: 3,
          borderRadius: 4,
          boxShadow: 6,
          background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
        }}
      >
        {/* Search/Filter Row */}
        

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
           {/* Filter Buttons Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            mb: 0,
            justifyContent: { xs: "center", md: "flex-start" }
          }}
        >
          <Button
            startIcon={<GroupsIcon sx={{ color: "#1976d2" }} />}
            sx={{
              color: "#1976d2",
              fontWeight: 700,
              bgcolor: "#e3f2fd",
              borderRadius: 3,
              px: 2,
              boxShadow: 1,
              fontFamily:"Josefin Sans",
              "&:hover": { bgcolor: "#bbdefb" }
            }}
            onClick={() => {
              setFilterSearch("");
              setFilterEvent("");
              setFilterDate("");
            }}
          >
            All Users
          </Button>
        </Box>
          <TextField
            placeholder="Search name, event, or mobile"
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            variant="outlined"
            sx={{
              bgcolor: "#f5faff",
              borderRadius: 2,
              minWidth: 520,
              boxShadow: "0 2px 8px #bbdefb88",
              "& .MuiOutlinedInput-root": {
                fontWeight: 600,
                fontSize: 18,
                color: "#1976d2",
                "& fieldset": { borderColor: "#90caf9" },
                "&:hover fieldset": { borderColor: "#1976d2" },
                "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 2 },
              },
              "& input": { py: 1.5 },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "#1976d2", mr: 1, fontSize: 28 }} />
              ),
            }}
          />
          <TextField
            select
            label="Event"
            value={filterEvent}
            onChange={e => setFilterEvent(e.target.value)}
            variant="outlined"
            sx={{
              bgcolor: "#f1f8e9",
              borderRadius: 4,
              minWidth: 180,
              boxShadow: "0 2px 8px #c8e6c988",
              "& .MuiOutlinedInput-root": {
                fontWeight: 600,
                fontSize: 18,
                fontFamily:"Josefin Sans",
                color: "#388e3c",
                "& fieldset": { borderColor: "#a5d6a7" },
                "&:hover fieldset": { borderColor: "#388e3c" },
                "&.Mui-focused fieldset": { borderColor: "#388e3c", borderWidth: 2 },
              },
            }}
          >
            <MenuItem value="" sx={{fontFamily:"Josefin Sans"}}>All Events</MenuItem>
            {eventOptions.map(option => (
              <MenuItem sx={{fontFamily:"Josefin Sans"}} key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Date"
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            variant="outlined"
            sx={{
              bgcolor: "#fff3e0",
              borderRadius: 4,
              minWidth: 180,
              boxShadow: "0 2px 8px #ffe0b288",
              "& .MuiOutlinedInput-root": {
                fontWeight: 600,
                fontSize: 18,
                color: "#d32f2f",
                "& fieldset": { borderColor: "#ffccbc" },
                "&:hover fieldset": { borderColor: "#d32f2f" },
                "&.Mui-focused fieldset": { borderColor: "#d32f2f", borderWidth: 2 },
              },
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

       

        {/* User Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#e3f2fd" }}>
                <TableCell sx={{ fontWeight: 700, color: "#1976d2",fontFamily:"Josefin Sans" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1976d2",fontFamily:"Josefin Sans" }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1976d2",fontFamily:"Josefin Sans" }}>Place</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1976d2" ,fontFamily:"Josefin Sans"}}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1976d2" ,fontFamily:"Josefin Sans" }}>Event Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#388e3c" ,fontFamily:"Josefin Sans"}}>Original Images</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#388e3c",fontFamily:"Josefin Sans"   }}>Selected Copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsersAdvanced.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "#888",fontFamily:"Josefin Sans" }}>
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsersAdvanced.map(user => (
                  <TableRow
                    key={user._id}
                    hover
                    sx={{
                      transition: "background 0.2s, transform 0.2s",
                      "&:hover": { background: "#e3f2fd", transform: "scale(1.01)" }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 ,fontFamily:"Josefin Sans"}}>{user.name}</TableCell>
                    <TableCell sx={{fontFamily:"Josefin Sans"}}>{user.mobile}</TableCell>
                    <TableCell sx={{fontFamily:"Josefin Sans"}}>{user.place}</TableCell>
                    <TableCell sx={{fontFamily:"Josefin Sans"}}>{user.event}</TableCell>
                    <TableCell sx={{fontFamily:"Josefin Sans"}}>{user.date ? user.date.slice(0, 10) : ""}</TableCell>
                    {/* Original Images column */}
                    <TableCell>
                      {Array.isArray(user.originalImages) && user.originalImages.length > 0 ? (
                        <Typography sx={{ color: "#388e3c", fontWeight: 700,fontFamily:"Josefin Sans" }}>
                          Uploaded
                        </Typography>
                      ) : (
                        <Typography sx={{ color: "#d32f2f", fontWeight: 700,fontFamily:"Josefin Sans" }}>
                          Not Uploaded
                        </Typography>
                      )}
                    </TableCell>
                    {/* Selected Copy column */}
                    <TableCell>
                      {Array.isArray(user.selectedCopyImages) && user.selectedCopyImages.length > 0 ? (
                        <Typography sx={{ color: "#388e3c", fontWeight: 700,fontFamily:"Josefin Sans" }}>
                          Copied
                        </Typography>
                      ) : (
                        <Typography sx={{ color: "#d32f2f", fontWeight: 700,fontFamily:"Josefin Sans" }}>
                          Not Selected
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

export default UserManagement;
